/**
 * excel.js — student bulk import/export (client-side, SheetJS).
 */
import * as XLSX from "xlsx";
// xlsx-js-style: نفس مكتبة SheetJS المستخدَمة فعليًا وبنجاح مثبَت في ملف
// Excel الخاص بـ"مراجعة الرصد" (Reports.jsx) لكتابة الألوان والحدود
// بشكل موثوق. نستخدمها هنا حصريًا لدالة تصدير التحليل الإحصائي (أسفل
// الملف) فقط — بقية دوال هذا الملف (استيراد/تصدير الطلاب، أرقام الجلوس)
// تبقى تمامًا كما هي بمكتبة "xlsx" الأساسية، بلا أي تغيير في سلوكها.
import * as XLSXStyle from "xlsx-js-style";

// Canonical (Arabic) headers written into the downloaded template.
export const TEMPLATE_HEADERS = [
  "الاسم", "الرقم القومي", "رقم الهوية / الإقامة",
  "تاريخ الميلاد", "محل الميلاد", "الجنس",
  "الصف", "الفصل", "القسم",
  "اللغة الأجنبية الأولى", "اللغة الأجنبية الثانية", "رقم الجلوس",
];

/**
 * Alias table: canonical field ← accepted Arabic header variants.
 * Matching is done via .includes(...) → tolerant to small typos & spacing.
 */
const FIELD_ALIASES = {
  name: ["اسم الطالب", "الاسم", "اسم"],
  national_id: ["الرقم القومي", "رقم قومي", "قومي"],
  identity_number: ["رقم الهوية", "الاقامة", "الإقامة", "هوية"],
  birthdate: ["تاريخ الميلاد", "تاريخ ميلاد", "الميلاد"],
  birth_place: ["محل الميلاد", "المحافظة", "محل ميلاد"],
  gender: ["الجنس", "النوع"],
  grade: ["الصف الدراسي", "الصف"],
  class: ["الفصل"],
  section: ["القسم"],
  foreign_lang1: ["اللغة الأجنبية الأولى", "لغة أولى", "لغة 1"],
  foreign_lang2: ["اللغة الأجنبية الثانية", "لغة ثانية", "لغة 2"],
  seat_number: ["رقم الجلوس", "جلوس"],
};

/** Trigger download of an empty student-import template. */
export function downloadStudentTemplate() {
  const rows = [TEMPLATE_HEADERS, TEMPLATE_HEADERS.map(() => "")];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
  ws["!rtl"] = true;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "الطلاب");
  XLSX.writeFile(wb, "قالب-استيراد-الطلاب.xlsx");
}

/**
 * Parse a spreadsheet file (xlsx/xls/csv) into a list of loose student objects.
 * Only recognized fields are kept; extras are ignored.
 */
export async function parseStudentExcel(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
  if (!raw || raw.length < 2) return [];

  // Map columns → canonical field names
  const headerRow = (raw[0] || []).map((h) => String(h || "").trim());
  const colMap = {}; // field → columnIndex
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    for (let i = 0; i < headerRow.length; i++) {
      const h = headerRow[i];
      if (!h) continue;
      if (aliases.some((a) => h === a || h.includes(a) || a.includes(h))) {
        colMap[field] = i;
        break;
      }
    }
  }

  const students = [];
  for (let r = 1; r < raw.length; r++) {
    const row = raw[r] || [];
    const obj = {};
    let hasAny = false;
    for (const [field, colIdx] of Object.entries(colMap)) {
      let v = row[colIdx];
      if (v == null || v === "") continue;
      // If Excel gave us a Date object (for birthdate), format ISO
      if (v instanceof Date && !Number.isNaN(v.getTime())) {
        v = v.toISOString().slice(0, 10);
      } else {
        v = String(v).trim();
      }
      if (v === "") continue;
      obj[field] = v;
      hasAny = true;
    }
    if (hasAny) students.push(obj);
  }
  return students;
}

// ══════════════════════ إدارة أرقام الجلوس (استبدال جماعي) ══════════════════════
// عمود "معرف داخلي" هنا هو مفتاح المطابقة الوحيد المعتمَد فعليًا (وليس
// الاسم ولا الترتيب) — مخفي بصريًا عبر ws['!cols'][i].hidden، فلا يظهر
// للمستخدم عاديًا لكنه يبقى داخل الملف نفسه ويُقرأ عند الرفع مرة أخرى.
const SEAT_TEMPLATE_HEADERS = ["م", "اسم الطالب", "الصف", "الفصل", "رقم الجلوس الحالي", "رقم الجلوس الجديد"];
const SEAT_HIDDEN_ID_HEADER = "معرف داخلي (لا تُعدِّل)";

/** تصدير نموذج Excel لاستبدال أرقام الجلوس جماعيًا — للطلاب المفلترين
 * حاليًا في شاشة إدارة أرقام الجلوس فقط. */
export function downloadSeatNumberTemplate(students, filename = "نموذج-أرقام-الجلوس.xlsx") {
  const headers = [...SEAT_TEMPLATE_HEADERS, SEAT_HIDDEN_ID_HEADER];
  const rows = [headers, ...students.map((s, i) => [
    i + 1, s.name, s.grade, s.class || "", s.seat_number || "", "", s.id,
  ])];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 5 }, { wch: 24 }, { wch: 16 }, { wch: 10 }, { wch: 16 }, { wch: 16 },
    { wch: 26, hidden: true },
  ];
  ws["!rtl"] = true;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "أرقام الجلوس");
  XLSX.writeFile(wb, filename);
}

/** تصدير كشف عادي (بدون عمود التعديل) — لزر "تصدير Excel" العادي. */
export function exportSeatNumbersList(students, filename = "كشف-أرقام-الجلوس.xlsx") {
  const headers = ["م", "اسم الطالب", "الصف", "الفصل", "رقم الجلوس"];
  const rows = [headers, ...students.map((s, i) => [i + 1, s.name, s.grade, s.class || "", s.seat_number || ""])];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 5 }, { wch: 24 }, { wch: 16 }, { wch: 10 }, { wch: 16 }];
  ws["!rtl"] = true;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "أرقام الجلوس");
  XLSX.writeFile(wb, filename);
}

/** قراءة ملف الاستبدال الجماعي المرفوع — المطابقة تتم حصريًا عبر عمود
 * "معرف داخلي" (student_id)، وليس الاسم أو رقم الجلوس الحالي أو الترتيب.
 * صفوف بلا معرّف داخلي صالح تُستبعَد فورًا (لا يمكن مطابقتها بأمان). */
export async function parseSeatNumberExcel(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
  if (!raw || raw.length < 2) return [];

  const headerRow = (raw[0] || []).map((h) => String(h || "").trim());
  const idCol = headerRow.findIndex((h) => h === SEAT_HIDDEN_ID_HEADER);
  const newSeatCol = headerRow.findIndex((h) => h === "رقم الجلوس الجديد");
  const curSeatCol = headerRow.findIndex((h) => h === "رقم الجلوس الحالي");
  if (idCol === -1) return []; // ملف غير مطابق لتنسيق النموذج، لا مطابقة آمنة ممكنة

  const rows = [];
  for (let r = 1; r < raw.length; r++) {
    const row = raw[r] || [];
    const student_id = String(row[idCol] || "").trim();
    if (!student_id) continue; // لا يوجد معرف داخلي = لا يمكن مطابقته بأمان، يُستبعَد
    rows.push({
      student_id,
      current_seat_number: curSeatCol !== -1 ? String(row[curSeatCol] || "").trim() : "",
      new_seat_number: newSeatCol !== -1 ? String(row[newSeatCol] || "").trim() : "",
    });
  }
  return rows;
}

// ══════════════════════ التحليل الإحصائي والتربوي — تصدير Excel ══════════════════════
// ملف متعدد الأوراق احترافي: ملخص، إحصاءات، توزيع مستويات الأداء، بيانات
// الطلاب، التحليل الكيفي، التوصيات. كل الأرقام تأتي جاهزة من /analysis
// (مصدر الحقيقة الوحيد لحساب الدرجات في Backend) — هذا الملف عرض/تنسيق
// فقط، بلا أي إعادة حساب.

const BRAND_TEAL = "0E7469";
const BRAND_TEAL_DARK = "0A5049";
const LIGHT_TINT = "EAF4F2";
const BORDER_GRAY = "CBD5E1";

const THIN_BORDER = {
  top: { style: "thin", color: { rgb: BORDER_GRAY } },
  bottom: { style: "thin", color: { rgb: BORDER_GRAY } },
  left: { style: "thin", color: { rgb: BORDER_GRAY } },
  right: { style: "thin", color: { rgb: BORDER_GRAY } },
};

function _cellRef(r, c) {
  return XLSXStyle.utils.encode_cell({ r, c });
}

/** يضبط نمط خلية واحدة (خلفية/خط/محاذاة/حدود) — يُستدعى بعد بناء الورقة
 * بـ aoa_to_sheet. لو نسخة SheetJS الحالية لا تكتب الأنماط فعليًا عند
 * الحفظ (بعض القيود التاريخية في النسخة المجانية)، الملف يبقى صالحًا
 * وقابلًا للفتح بلا أي كسر — فقط بلا الألوان، دون أي تأثير على البيانات. */
function _setStyle(ws, r, c, style) {
  const ref = _cellRef(r, c);
  if (!ws[ref]) ws[ref] = { t: "s", v: "" };
  ws[ref].s = { ...(ws[ref].s || {}), ...style };
}

function _titleStyle() {
  return {
    font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: BRAND_TEAL_DARK } },
    alignment: { horizontal: "center", vertical: "center" },
  };
}

function _headerStyle() {
  return {
    font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: BRAND_TEAL } },
    alignment: { horizontal: "center", vertical: "center" },
    border: THIN_BORDER,
  };
}

function _cellDataStyle(align = "center") {
  return { alignment: { horizontal: align, vertical: "center" }, border: THIN_BORDER };
}

function _labelStyle() {
  return { font: { bold: true, color: { rgb: BRAND_TEAL_DARK } }, fill: { fgColor: { rgb: LIGHT_TINT } }, border: THIN_BORDER };
}

/** يطبّق: تنسيق صف العنوان الرئيسي (الصف 0)، صف رؤوس الأعمدة (headerRow)،
 * وحدود + محاذاة لكل خلايا البيانات حتى آخر صف — على أي ورقة AOA قياسية. */
function _applyTableTheme(ws, { titleCols, headerRow, dataStartRow, dataEndRow, numCols, centerCols = [] }) {
  for (let c = 0; c < titleCols; c++) _setStyle(ws, 0, c, _titleStyle());
  if (headerRow != null) {
    for (let c = 0; c < numCols; c++) _setStyle(ws, headerRow, c, _headerStyle());
  }
  for (let r = dataStartRow; r <= dataEndRow; r++) {
    for (let c = 0; c < numCols; c++) {
      _setStyle(ws, r, c, _cellDataStyle(centerCols.includes(c) || centerCols.length === 0 ? "center" : "right"));
    }
  }
}

function _styledSheet(rows, colWidths, mergeRanges = [], rowHeights = null) {
  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  ws["!cols"] = colWidths.map((w) => ({ wch: w }));
  ws["!rtl"] = true;
  if (mergeRanges.length) ws["!merges"] = mergeRanges;
  if (rowHeights) ws["!rows"] = rowHeights;
  return ws;
}

/**
 * تصدير تقرير التحليل الإحصائي كملف Excel من 6 أوراق، بتنسيق احترافي
 * (ألوان هوية النظام، رؤوس بارزة، حدود، محاذاة).
 * @param {object} analysis - استجابة GET /analysis كما هي (لا تعديل عليها).
 * @param {object} meta - { schoolName, stage, grade, className, subjectName, certTypeLabel, year }
 * @param {string} qualitativeText - النص التفسيري الكيفي (مبني من نفس analysis في الواجهة).
 * @param {string} recommendationText - التوصية التربوية (مبنية من نفس analysis في الواجهة).
 */
export function exportAnalysisExcel(analysis, meta, qualitativeText, recommendationText, filename = "التحليل-الإحصائي.xlsx") {
  const wb = XLSXStyle.utils.book_new();

  // ── Sheet 1: الملخص ──
  const summaryRows = [
    ["تقرير التحليل الإحصائي والتربوي", ""],
    ["", ""],
    ["المدرسة", meta.schoolName || "—"],
    ["العام الدراسي", meta.year || "—"],
    ["المرحلة", meta.stage || "—"],
    ["الصف", meta.grade || "—"],
    ["الفصل", meta.className || "كل الفصول"],
    ["المادة", meta.subjectName || "كل المواد"],
    ["الفترة", meta.certTypeLabel || "—"],
    ["", ""],
    ["المؤشر", "القيمة"],
    ["إجمالي عدد الطلاب", analysis.count],
    ["عدد الطلاب المُقيَّمين", analysis.evaluated_count],
    ["عدد الطلاب المستبعدين (بيانات ناقصة)", analysis.excluded_count],
    ["أعلى درجة (نسبة)", analysis.highest_percentage],
    ["أقل درجة (نسبة)", analysis.lowest_percentage],
    ["المتوسط الحسابي", analysis.average_percentage],
    ["الوسيط", analysis.median_percentage],
    ["الانحراف المعياري", analysis.std_dev],
    ["نسبة النجاح %", analysis.success_rate],
    ["نسبة الإتقان %", analysis.mastery_rate],
  ];
  const wsSummary = _styledSheet(summaryRows, [32, 20], [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }]);
  _setStyle(wsSummary, 0, 0, _titleStyle()); _setStyle(wsSummary, 0, 1, _titleStyle());
  for (let r = 2; r <= 8; r++) { _setStyle(wsSummary, r, 0, _labelStyle()); _setStyle(wsSummary, r, 1, _cellDataStyle("right")); }
  for (let c = 0; c < 2; c++) _setStyle(wsSummary, 10, c, _headerStyle());
  for (let r = 11; r <= 19; r++) { _setStyle(wsSummary, r, 0, _labelStyle()); _setStyle(wsSummary, r, 1, _cellDataStyle("center")); }
  XLSXStyle.utils.book_append_sheet(wb, wsSummary, "الملخص");

  // ── Sheet 2: الإحصاءات التفصيلية ──
  const statsHeaders = ["إجمالي الطلاب", "المُقيَّمون", "المستبعدون", "أعلى درجة", "أقل درجة", "المتوسط", "الوسيط", "الانحراف المعياري", "نسبة النجاح %", "نسبة الإتقان %"];
  const statsRows = [
    ["الإحصاءات التفصيلية"],
    [],
    statsHeaders,
    [
      analysis.count, analysis.evaluated_count, analysis.excluded_count,
      analysis.highest_percentage, analysis.lowest_percentage, analysis.average_percentage,
      analysis.median_percentage, analysis.std_dev, analysis.success_rate, analysis.mastery_rate,
    ],
  ];
  const wsStats = _styledSheet(statsRows, [14, 12, 12, 12, 12, 12, 10, 16, 14, 14],
    [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }]);
  _applyTableTheme(wsStats, { titleCols: 10, headerRow: 2, dataStartRow: 3, dataEndRow: 3, numCols: 10 });
  XLSXStyle.utils.book_append_sheet(wb, wsStats, "الإحصاءات");

  // ── Sheet 3: توزيع مستويات الأداء (لون مطابق للون المستوى فعليًا) ──
  const bandRows = [
    ["توزيع الطلاب حسب مستويات الأداء"],
    [],
    ["المستوى", "النطاق", "عدد الطلاب", "النسبة %"],
    ...analysis.band_order.map((lbl) => {
      const d = analysis.band_distribution_detailed[lbl];
      return [lbl, d.range, d.count, d.percentage];
    }),
  ];
  const wsBands = _styledSheet(bandRows, [22, 14, 14, 12], [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }]);
  _applyTableTheme(wsBands, { titleCols: 4, headerRow: 2, dataStartRow: 3, dataEndRow: 2 + analysis.band_order.length, numCols: 4 });
  // تلوين خلية "المستوى" بنفس لون المستوى فعليًا (كما في الشاشة والـPDF)
  analysis.band_order.forEach((lbl, i) => {
    const d = analysis.band_distribution_detailed[lbl];
    const rgb = (d.color || "#94A3B8").replace("#", "");
    _setStyle(wsBands, 3 + i, 0, {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb } },
      alignment: { horizontal: "center", vertical: "center" },
      border: THIN_BORDER,
    });
  });
  XLSXStyle.utils.book_append_sheet(wb, wsBands, "توزيع الأداء");

  // ── Sheet 4: بيانات الطلاب ودرجاتهم (تلوين حالة النجاح/عدم النجاح) ──
  const studentHeaders = ["م", "اسم الطالب", "رقم الجلوس", "الفصل", "الدرجة/النسبة %", "التقدير", "الترتيب", "الحالة"];
  const studentRows = [
    ["بيانات الطلاب ودرجاتهم"],
    [],
    studentHeaders,
    ...analysis.students.map((s, i) => [
      i + 1, s.name, s.seat_number || "—", s.class || "—",
      s.percentage ?? "—", s.band ?? "—", s.rank ?? "—",
      s.passed ? "ناجح" : "غير ناجح",
    ]),
  ];
  const wsStudents = _styledSheet(studentRows, [5, 26, 12, 10, 14, 14, 10, 12], [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }]);
  _applyTableTheme(wsStudents, { titleCols: 8, headerRow: 2, dataStartRow: 3, dataEndRow: 2 + analysis.students.length, numCols: 8, centerCols: [0, 2, 3, 4, 5, 6, 7] });
  analysis.students.forEach((s, i) => {
    const r = 3 + i;
    _setStyle(wsStudents, r, 1, _cellDataStyle("right")); // اسم الطالب محاذاة يمين
    _setStyle(wsStudents, r, 7, {
      font: { bold: true, color: { rgb: s.passed ? "166534" : "991B1B" } },
      fill: { fgColor: { rgb: s.passed ? "DCFCE7" : "FEE2E2" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: THIN_BORDER,
    });
  });
  XLSXStyle.utils.book_append_sheet(wb, wsStudents, "بيانات الطلاب");

  // ── Sheet 5: التحليل الكيفي ──
  const qualRows = [["التحليل الكيفي"], [], [qualitativeText || "—"]];
  const wsQual = _styledSheet(qualRows, [110],
    [{ s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 0 } }],
    { 2: { hpt: 60 } });
  _setStyle(wsQual, 0, 0, _titleStyle());
  _setStyle(wsQual, 2, 0, { alignment: { horizontal: "right", vertical: "center", wrapText: true }, font: { sz: 12 }, border: THIN_BORDER });
  XLSXStyle.utils.book_append_sheet(wb, wsQual, "التحليل الكيفي");

  // ── Sheet 6: التوصيات ──
  const recRows = [["التوصيات التربوية"], [], [recommendationText || "—"]];
  const wsRec = _styledSheet(recRows, [110],
    [{ s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 0 } }],
    { 2: { hpt: 60 } });
  _setStyle(wsRec, 0, 0, _titleStyle());
  _setStyle(wsRec, 2, 0, { alignment: { horizontal: "right", vertical: "center", wrapText: true }, font: { sz: 12 }, border: THIN_BORDER });
  XLSXStyle.utils.book_append_sheet(wb, wsRec, "التوصيات");

  XLSXStyle.writeFile(wb, filename, { cellStyles: true });
}
